/** Cai session authentication and worker-owned conversation turns. */
import { type AgentEvent } from '@/lib/agent/events'
import { followAgentTurn, type FollowTurnOptions, TurnStreamError } from '@/lib/agent/stream'
import { getNotificationPermission, NotificationPermissionState } from '@/lib/desktopNotifications'
import { followSseChannel } from '@/lib/sseChannel'
import type { PageContextPayload } from '@/models/agentConversation'
import { type AnswerFeedback, AnswerRating, FlagReason } from '@/lib/agent/answerFeedback'

import {
  type ActiveTurn,
  type CaiAuth as SharedCaiAuth,
  type CaiMemory,
  type ChatMessage,
  ChatRole,
  type ConversationSnapshot,
  DocumentFileFormat,
  type GeneratedFile,
  type ScheduledQuestion,
  type SharedDocument,
  type WebsiteVerification,
  WebsiteCodeSentBy,
} from '../../../contracts/cai'

export {
  type ActiveTurn,
  type CaiMemory,
  type ChatMessage,
  ChatRole,
  type ConversationSnapshot,
  DocumentFileFormat,
  fileFormatLabel,
  type GeneratedFile,
  MAX_FILES_PER_MESSAGE,
  MAX_MESSAGE_LENGTH,
  type SharedDocument,
  type WebsiteVerification,
  WebsiteCodeSentBy,
} from '../../../contracts/cai'

const AUTH_ENDPOINT = '/api/v1/cai-agent/auth'
const LOGOUT_ENDPOINT = '/api/v1/cai-agent/auth/logout'
const CONVERSATION_ENDPOINT = '/api/v1/cai-agent/conversation'
const MESSAGES_ENDPOINT = '/api/v1/cai-agent/conversation/messages'
const DOCUMENTS_ENDPOINT = '/api/v1/cai-agent/conversation/documents'
const MEMORIES_ENDPOINT = '/api/v1/cai-agent/memories'
const SCHEDULED_TASKS_ENDPOINT = '/api/v1/cai-agent/scheduled-tasks'
const WEBSITE_LOGINS_ENDPOINT = '/api/v1/cai-agent/website-logins'
const WEBSITE_VERIFICATIONS_ENDPOINT = '/api/v1/cai-agent/website-verifications'

/** A transcript message as the browser reads it: for Cai staff, an answer also carries their rating and trace link. */
export type CaiChatMessage = ChatMessage & {
  canRate?: boolean
  feedback?: AnswerFeedback | null
  traceUrl?: string | null
}

export type CaiConversationSnapshot = Omit<ConversationSnapshot, 'messages'> & { messages: CaiChatMessage[] }

export type CaiAuth = SharedCaiAuth & {
  account_id: string | null
  session_authenticated: boolean
  /** Cai staff, who get the product shell's Console tab whether or not they are enrolled in Cai. */
  console_enabled: boolean
}

export const CAI_AGENT_PATH = '/cai-agent'
export const CAI_AGENT_LOGIN_PATH = '/cai-agent/login'

export type WebsiteLogin = {
  id: string
  origin: string
  url: string
  /** What the person calls the website, such as WEX; empty when they gave none. */
  name: string
}

export type WebsiteCredentials = { url: string; name: string; username: string; password: string }

function writeHeaders(csrfToken: string): Record<string, string> {
  return { 'Content-Type': 'application/json', 'X-CSRFToken': csrfToken }
}

function parseAuth(body: unknown): CaiAuth {
  if (
    typeof body !== 'object' ||
    body === null ||
    !('csrf_token' in body) ||
    typeof body.csrf_token !== 'string' ||
    !('authenticated' in body) ||
    typeof body.authenticated !== 'boolean'
  ) {
    throw new Error('Unexpected Cai auth response')
  }
  const fields = body as Record<string, unknown>
  return {
    csrf_token: body.csrf_token,
    account_id: typeof fields.account_id === 'string' ? fields.account_id : null,
    authenticated: body.authenticated,
    session_authenticated: fields.session_authenticated === true,
    console_enabled: fields.console_enabled === true,
    first_name: typeof fields.first_name === 'string' ? fields.first_name : '',
    email: typeof fields.email === 'string' ? fields.email : '',
    conversation_mode: fields.conversation_mode === 'MULTIPLE' ? 'MULTIPLE' : 'SINGLE',
  }
}

export async function getCaiAuth(signal?: AbortSignal): Promise<CaiAuth> {
  // The browser's time zone, so Cai's own scheduled tasks run in it even before the person asks anything.
  const response = await fetch(AUTH_ENDPOINT, {
    credentials: 'same-origin',
    headers: { 'X-Cai-Time-Zone': Intl.DateTimeFormat().resolvedOptions().timeZone },
    signal,
  })
  if (!response.ok) throw response
  return parseAuth(await response.json())
}

export async function requestCaiSignInCode(csrfToken: string, email: string): Promise<void> {
  const response = await fetch(`${AUTH_ENDPOINT}/code`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: writeHeaders(csrfToken),
    body: JSON.stringify({ email }),
  })
  if (!response.ok) throw response
}

export async function verifyCaiSignInCode(
  csrfToken: string,
  credentials: { email: string; code: string }
): Promise<CaiAuth> {
  const response = await fetch(AUTH_ENDPOINT, {
    method: 'POST',
    credentials: 'same-origin',
    headers: writeHeaders(csrfToken),
    body: JSON.stringify(credentials),
  })
  if (!response.ok) throw response
  return parseAuth(await response.json())
}

export async function signOut(csrfToken: string): Promise<void> {
  const response = await fetch(LOGOUT_ENDPOINT, {
    method: 'POST',
    credentials: 'same-origin',
    headers: writeHeaders(csrfToken),
  })
  if (!response.ok) throw response
}

export async function getConversation(
  signal?: AbortSignal,
  before?: number
): Promise<CaiConversationSnapshot> {
  const endpoint = before === undefined ? CONVERSATION_ENDPOINT : `${CONVERSATION_ENDPOINT}?before=${before}`
  const response = await fetch(endpoint, { credentials: 'same-origin', signal })
  if (!response.ok) throw response
  const body: unknown = await response.json()
  if (typeof body !== 'object' || body === null || !('messages' in body) || !Array.isArray(body.messages)) {
    throw new Error('Unexpected Cai conversation response')
  }
  const cursor = 'next_before' in body ? body.next_before : null
  if (cursor !== null && (typeof cursor !== 'number' || !Number.isSafeInteger(cursor) || cursor < 0)) {
    throw new Error('Unexpected Cai conversation cursor')
  }
  const messages = body.messages.map((message: unknown, index: number) => {
    const fields = (message ?? {}) as Record<string, unknown>
    if (
      (fields.role !== ChatRole.User && fields.role !== ChatRole.Assistant) ||
      typeof fields.content !== 'string'
    ) {
      throw new Error('Unexpected Cai conversation message')
    }
    // Older responses lack ids and pagination; keep their transcript readable during a deploy.
    const messageId = typeof fields.id === 'string' ? fields.id : `legacy-${index}`
    return {
      id: messageId,
      role: fields.role,
      content: fields.content,
      // A server from before spreadsheets delivers its PDFs under pdf.
      ...(fields.file !== undefined || fields.pdf !== undefined
        ? { file: parseGeneratedFile(fields.file ?? fields.pdf, messageId) }
        : {}),
      ...(Array.isArray(fields.documents) ? { documents: fields.documents.map(parseDocument) } : {}),
      ...scheduledQuestion(fields.scheduled_task),
      ...(fields.can_rate === true
        ? {
            canRate: true,
            feedback: parseAnswerFeedback(fields.feedback),
            traceUrl: typeof fields.trace_url === 'string' ? fields.trace_url : null,
          }
        : {}),
    }
  })
  const active = 'active_turn' in body ? body.active_turn : null
  if (
    active !== null &&
    (typeof active !== 'object' ||
      !('turn_id' in active) ||
      typeof active.turn_id !== 'string' ||
      !('message' in active) ||
      typeof active.message !== 'string')
  ) {
    throw new Error('Unexpected Cai active turn response')
  }
  const pending = active as {
    turn_id: string
    message: string
    documents?: unknown
    scheduled_task?: unknown
  } | null
  const activeTurn: ActiveTurn | null = pending && {
    turn_id: pending.turn_id,
    message: pending.message,
    documents: Array.isArray(pending.documents) ? pending.documents.map(parseDocument) : [],
    ...scheduledQuestion(pending.scheduled_task),
  }
  const documents =
    'documents' in body && Array.isArray(body.documents) ? body.documents.map(parseDocument) : []
  const website_verification =
    'website_verification' in body ? parseWebsiteVerification(body.website_verification) : null
  const conversation_id =
    'conversation_id' in body && typeof body.conversation_id === 'string' ? body.conversation_id : null
  return {
    messages,
    next_before: cursor,
    active_turn: activeTurn,
    documents,
    website_verification,
    conversation_id,
  }
}

/** The task that asked a question, under scheduledTask; nothing for a question the person asked. */
function scheduledQuestion(value: unknown): { scheduledTask?: ScheduledQuestion } {
  const fields = (value ?? {}) as Record<string, unknown>
  if (typeof fields.title !== 'string') return {}
  return { scheduledTask: { id: typeof fields.id === 'string' ? fields.id : null, title: fields.title } }
}

function parseAnswerFeedback(value: unknown): AnswerFeedback | null {
  if (typeof value !== 'object' || value === null) return null
  const fields = value as Record<string, unknown>
  const rating = Object.values(AnswerRating).find((known) => known === fields.rating)
  const flagReason = Object.values(FlagReason).find((known) => known === fields.flag_reason) ?? ''
  if (!rating || typeof fields.comment !== 'string') throw new Error('Unexpected Cai answer feedback')
  return { rating, flagReason, comment: fields.comment }
}

/** Save the signed-in staff member's rating of one of Cai's answers, replacing any earlier one. */
export async function saveAnswerFeedback(
  csrfToken: string,
  messageId: string,
  feedback: AnswerFeedback
): Promise<AnswerFeedback | null> {
  const response = await fetch(`${MESSAGES_ENDPOINT}/${messageId}/feedback`, {
    method: 'PUT',
    credentials: 'same-origin',
    headers: writeHeaders(csrfToken),
    body: JSON.stringify({
      rating: feedback.rating,
      flag_reason: feedback.flagReason,
      comment: feedback.comment,
    }),
  })
  if (!response.ok) throw response
  return parseAnswerFeedback(await response.json())
}

export async function clearAnswerFeedback(csrfToken: string, messageId: string): Promise<void> {
  const response = await fetch(`${MESSAGES_ENDPOINT}/${messageId}/feedback`, {
    method: 'DELETE',
    credentials: 'same-origin',
    headers: writeHeaders(csrfToken),
  })
  if (!response.ok) throw response
}

function parseWebsiteVerification(value: unknown): WebsiteVerification | null {
  if (typeof value !== 'object' || value === null) return null
  const fields = value as Record<string, unknown>
  if (
    typeof fields.id !== 'string' ||
    typeof fields.origin !== 'string' ||
    typeof fields.expires_at !== 'string'
  ) {
    throw new Error('Unexpected Cai website verification')
  }
  const sentBy = Object.values(WebsiteCodeSentBy).find((channel) => channel === fields.sent_by)
  return {
    id: fields.id,
    origin: fields.origin,
    expires_at: fields.expires_at,
    sent_by: sentBy ?? WebsiteCodeSentBy.Unknown,
  }
}

export async function getMemories(signal?: AbortSignal): Promise<CaiMemory[]> {
  const response = await fetch(MEMORIES_ENDPOINT, { credentials: 'same-origin', signal })
  if (!response.ok) throw response
  const body: unknown = await response.json()
  if (typeof body !== 'object' || body === null || !('memories' in body) || !Array.isArray(body.memories)) {
    throw new Error('Unexpected Cai memories response')
  }
  return body.memories.map((memory: unknown) => {
    const fields = (memory ?? {}) as Record<string, unknown>
    if (
      typeof fields.id !== 'string' ||
      typeof fields.content !== 'string' ||
      typeof fields.kind !== 'string' ||
      typeof fields.date_created !== 'string' ||
      typeof fields.date_updated !== 'string'
    ) {
      throw new Error('Unexpected Cai memory')
    }
    return {
      id: fields.id,
      content: fields.content,
      kind: fields.kind,
      date_created: fields.date_created,
      date_updated: fields.date_updated,
    }
  })
}

export async function forgetMemory(csrfToken: string, memoryId: string, signal?: AbortSignal): Promise<void> {
  const response = await fetch(`${MEMORIES_ENDPOINT}/${encodeURIComponent(memoryId)}`, {
    method: 'DELETE',
    credentials: 'same-origin',
    headers: writeHeaders(csrfToken),
    signal,
  })
  if (!response.ok) throw response
}

export enum ScheduledTaskStatus {
  Active = 'ACTIVE',
  Paused = 'PAUSED',
  Ended = 'ENDED',
}

/** A task Cai runs on its own on a schedule; schedule is already in words, such as Every Monday at 9:00 AM. */
export type ScheduledTask = {
  id: string
  title: string
  instruction: string
  schedule: string
  status: ScheduledTaskStatus
  run_count: number
  max_runs: number | null
  ends_at: string | null
  last_run_at: string | null
  /** One of the tasks Cai offers every account, which can be turned off but not deleted. */
  built_in: boolean
}

function parseScheduledTask(value: unknown): ScheduledTask {
  const fields = (value ?? {}) as Record<string, unknown>
  const status = Object.values(ScheduledTaskStatus).find((known) => known === fields.status)
  if (
    typeof fields.id !== 'string' ||
    typeof fields.title !== 'string' ||
    typeof fields.instruction !== 'string' ||
    typeof fields.schedule !== 'string' ||
    status === undefined ||
    typeof fields.run_count !== 'number'
  ) {
    throw new Error('Unexpected Cai scheduled task')
  }
  return {
    id: fields.id,
    title: fields.title,
    instruction: fields.instruction,
    schedule: fields.schedule,
    status,
    run_count: fields.run_count,
    max_runs: typeof fields.max_runs === 'number' ? fields.max_runs : null,
    ends_at: typeof fields.ends_at === 'string' ? fields.ends_at : null,
    last_run_at: typeof fields.last_run_at === 'string' ? fields.last_run_at : null,
    built_in: fields.built_in === true,
  }
}

export async function getScheduledTasks(signal?: AbortSignal): Promise<ScheduledTask[]> {
  const response = await fetch(SCHEDULED_TASKS_ENDPOINT, { credentials: 'same-origin', signal })
  if (!response.ok) throw response
  const body: unknown = await response.json()
  if (typeof body !== 'object' || body === null || !('tasks' in body) || !Array.isArray(body.tasks)) {
    throw new Error('Unexpected Cai scheduled tasks response')
  }
  return body.tasks.map(parseScheduledTask)
}

/** Turn a task on or off; returns it as the server now has it. */
export async function setScheduledTaskStatus(
  csrfToken: string,
  taskId: string,
  status: ScheduledTaskStatus.Active | ScheduledTaskStatus.Paused
): Promise<ScheduledTask> {
  const response = await fetch(`${SCHEDULED_TASKS_ENDPOINT}/${encodeURIComponent(taskId)}`, {
    method: 'PATCH',
    credentials: 'same-origin',
    headers: writeHeaders(csrfToken),
    body: JSON.stringify({ status }),
  })
  if (!response.ok) throw response
  const body: unknown = await response.json()
  if (typeof body !== 'object' || body === null || !('task' in body)) {
    throw new Error('Unexpected Cai scheduled task response')
  }
  return parseScheduledTask(body.task)
}

export async function deleteScheduledTask(csrfToken: string, taskId: string): Promise<void> {
  const response = await fetch(`${SCHEDULED_TASKS_ENDPOINT}/${encodeURIComponent(taskId)}`, {
    method: 'DELETE',
    credentials: 'same-origin',
    headers: writeHeaders(csrfToken),
  })
  if (!response.ok) throw response
}

/** Close out the conversation, so the next message starts a new one. */
export async function startNewConversation(csrfToken: string): Promise<void> {
  const response = await fetch(CONVERSATION_ENDPOINT, {
    method: 'DELETE',
    credentials: 'same-origin',
    headers: writeHeaders(csrfToken),
  })
  if (!response.ok) throw response
}

/**
 * Submit once, with the ids of shared files sent with it and the page it was asked from; a refusal
 * throws before an active turn is created in the UI.
 */
export async function sendMessage(
  csrfToken: string,
  message: string,
  signal?: AbortSignal,
  documentIds: string[] = [],
  pageContext: PageContextPayload | null = null
): Promise<string> {
  const response = await fetch(MESSAGES_ENDPOINT, {
    method: 'POST',
    credentials: 'same-origin',
    headers: writeHeaders(csrfToken),
    body: JSON.stringify({
      message,
      ...(documentIds.length > 0 ? { document_ids: documentIds } : {}),
      ...(pageContext ? { page_context: pageContext } : {}),
      time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }),
    signal,
  })
  if (!response.ok) throw response
  const accepted: unknown = await response.json()
  if (
    typeof accepted !== 'object' ||
    accepted === null ||
    !('turn_id' in accepted) ||
    typeof accepted.turn_id !== 'string'
  )
    throw new Error('Unexpected Cai turn response')
  return accepted.turn_id
}

/** A message Cai posted on its own, such as when a connection's first sync finished, that no tab has shown yet. */
export type UnreadMessage = {
  id: string
  content: string
}

/** Which conversation the account's chat shows, its newest message and its live turn; each is null when there is none. */
export type ConversationVersion = {
  conversation_id: string | null
  last_message_id: string | null
  active_turn_id: string | null
  /** Oldest first; absent from a server older than this page. */
  unread_messages?: UnreadMessage[]
}

const conversationListeners = new Set<(version: ConversationVersion) => void>()
let latestVersion: ConversationVersion | null = null
let stopConversationStream: (() => void) | null = null

/**
 * Follow the conversation's version, sent on connect and on every change from any tab or device; returns a stop
 * function. Every reader in the tab shares one stream, and one that joins late gets the latest version at once.
 * With desktop notifications allowed, the stream stays open behind other tabs so Cai's own messages can announce
 * themselves.
 */
export function followConversation(onVersion: (version: ConversationVersion) => void): () => void {
  conversationListeners.add(onVersion)
  const joined = latestVersion
  if (joined) queueMicrotask(() => conversationListeners.has(onVersion) && onVersion(joined))
  stopConversationStream ??= followSseChannel(
    `${CONVERSATION_ENDPOINT}/events`,
    (event) => {
      if (event.type !== 'conversation') return
      const version = event.data as ConversationVersion
      latestVersion = version
      conversationListeners.forEach((listener) => listener(version))
    },
    { whileHidden: () => getNotificationPermission() === NotificationPermissionState.GRANTED }
  )
  return () => {
    conversationListeners.delete(onVersion)
    if (conversationListeners.size > 0) return
    stopConversationStream?.()
    stopConversationStream = null
    latestVersion = null
  }
}

/** Tell every tab these messages of Cai's own have been on screen, so none shows them as unread. */
export async function markMessagesRead(csrfToken: string, messageIds: string[]): Promise<void> {
  const response = await fetch(`${CONVERSATION_ENDPOINT}/read`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: writeHeaders(csrfToken),
    body: JSON.stringify({ message_ids: messageIds }),
  })
  if (!response.ok) throw response
}

export async function streamTurn(
  turnId: string,
  onEvent: (event: AgentEvent) => void,
  options: FollowTurnOptions = {}
): Promise<void> {
  await followAgentTurn(
    async (fromSeq, signal) => {
      const response = await fetch(`${CONVERSATION_ENDPOINT}/turns/${turnId}/stream?from_seq=${fromSeq}`, {
        credentials: 'same-origin',
        signal,
      })
      if (!response.ok) throw new TurnStreamError(response.status)
      if (!response.body) throw new Error('Cai turn stream has no body')
      return response.body
    },
    onEvent,
    options
  )
}

export async function stopTurn(csrfToken: string, turnId: string): Promise<boolean> {
  const response = await fetch(`${CONVERSATION_ENDPOINT}/turns/${turnId}/stop`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: writeHeaders(csrfToken),
  })
  if (!response.ok) throw response
  const stopped: { stopping: boolean } = await response.json()
  return stopped.stopping
}

const QUICKBOOKS_ENDPOINT = '/api/v1/integrations/quickbooks'

/** The query parameter the connect callback appends on its way back, holding how the attempt ended. */
export const QUICKBOOKS_OUTCOME_PARAM = 'quickbooks'

/** Where a connect started from the chat returns, so the browser comes back to the chat rather than Connect. */
export const CAI_AGENT_CHAT_RETURN_PATH = `${CAI_AGENT_PATH}?from=chat`

/** How often Connect reads a connection on its first sync again; the chat hears of the finish from Cai's message. */
export const FIRST_SYNC_CHECK_MS = 15_000

/** The line Cai ends an answer with to offer connecting QuickBooks; the page draws it as a button. */
export const QUICKBOOKS_OFFER_MARKER = '[[connect-quickbooks]]'

export type QuickBooksConnection = {
  /** This deployment holds Intuit credentials; without them there is nothing to connect. */
  configured: boolean
  connected: boolean
  /** Intuit stopped honoring the grant, so the account has to connect again. */
  needsReconnect: boolean
  /** When the copy of the books was last caught up; null before the first sync finishes. */
  booksSyncedThrough: Date | null
  /** While the first sync runs, the day its copy of the transactions reaches back to; null otherwise. */
  booksCopiedFrom: Date | null
}

export type QuickBooksStatus = { csrfToken: string; connection: QuickBooksConnection }

function parseQuickBooksConnection(body: unknown): QuickBooksConnection {
  const fields = (typeof body === 'object' && body !== null ? body : {}) as Record<string, unknown>
  if (typeof fields.provider === 'string' && fields.provider !== 'quickbooks') {
    return {
      configured: false,
      connected: false,
      needsReconnect: false,
      booksSyncedThrough: null,
      booksCopiedFrom: null,
    }
  }
  const connection = (
    typeof fields.connection === 'object' && fields.connection !== null ? fields.connection : {}
  ) as Record<string, unknown>
  return {
    configured: connection.configured === true,
    connected: connection.state === 'ACTIVE',
    needsReconnect: connection.state === 'NEEDS_REAUTHORIZATION',
    booksSyncedThrough:
      typeof connection.books_synced_through === 'string' ? new Date(connection.books_synced_through) : null,
    // A bare date parses as UTC midnight, which an evening timezone shows as the day before.
    booksCopiedFrom:
      typeof connection.books_copied_from === 'string'
        ? new Date(`${connection.books_copied_from}T00:00:00`)
        : null,
  }
}

/** Throws the raw Response on refusal: a 403 means QuickBooks is switched off for this deployment. */
export async function getQuickBooksStatus(signal?: AbortSignal): Promise<QuickBooksStatus> {
  const response = await fetch(`${QUICKBOOKS_ENDPOINT}/status`, { credentials: 'same-origin', signal })
  if (!response.ok) throw response
  const body: unknown = await response.json()
  const csrfToken = typeof body === 'object' && body !== null && 'csrf_token' in body ? body.csrf_token : null
  if (typeof csrfToken !== 'string') throw new Error('Unexpected QuickBooks status response')
  return { csrfToken, connection: parseQuickBooksConnection(body) }
}

/** Start connecting and return Intuit's consent page; Intuit sends the browser back to returnTo. */
export async function startQuickBooksConnect(csrfToken: string, returnTo: string): Promise<string> {
  const response = await fetch(`${QUICKBOOKS_ENDPOINT}/connect`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: writeHeaders(csrfToken),
    body: JSON.stringify({ return_to: returnTo }),
  })
  if (!response.ok) throw response
  const body: unknown = await response.json()
  const url =
    typeof body === 'object' && body !== null && 'authorization_url' in body ? body.authorization_url : null
  if (typeof url !== 'string') throw new Error('Unexpected QuickBooks connect response')
  return url
}

/** Revoke the account's QuickBooks grant with Intuit and return where the connection now stands. */
export async function disconnectQuickBooks(csrfToken: string): Promise<QuickBooksConnection> {
  const response = await fetch(`${QUICKBOOKS_ENDPOINT}/disconnect`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: writeHeaders(csrfToken),
  })
  if (!response.ok) throw response
  return parseQuickBooksConnection(await response.json())
}

function isDocumentFileFormat(value: unknown): value is DocumentFileFormat {
  return Object.values(DocumentFileFormat).includes(value as DocumentFileFormat)
}

function parseDocument(body: unknown): SharedDocument {
  const fields = (body ?? {}) as Record<string, unknown>
  // A server from before spreadsheets shared only PDFs, so its documents carry no format.
  const fileFormat = fields.file_format ?? DocumentFileFormat.Pdf
  if (
    typeof fields.id !== 'string' ||
    typeof fields.filename !== 'string' ||
    !isDocumentFileFormat(fileFormat) ||
    typeof fields.page_count !== 'number'
  ) {
    throw new Error('Unexpected Cai document response')
  }
  return { id: fields.id, filename: fields.filename, file_format: fileFormat, page_count: fields.page_count }
}

function parseGeneratedFile(body: unknown, messageId: string): GeneratedFile {
  const document = parseDocument(body)
  const fields = body as Record<string, unknown>
  const own = `${MESSAGES_ENDPOINT}/${encodeURIComponent(messageId)}`
  // A server from before spreadsheets serves its PDFs at /pdf.
  if (fields.download_url !== `${own}/file` && fields.download_url !== `${own}/pdf`) {
    throw new Error('Unexpected Cai file download URL')
  }
  return { ...document, download_url: fields.download_url }
}

/** Upload a PDF, CSV or Excel file; Cai reads it once, and every later message in this conversation can draw on it. */
export async function shareDocument(
  csrfToken: string,
  file: File,
  signal?: AbortSignal
): Promise<SharedDocument> {
  const form = new FormData()
  form.append('file', file)
  // No Content-Type: the browser writes the multipart boundary into it.
  const response = await fetch(DOCUMENTS_ENDPOINT, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'X-CSRFToken': csrfToken },
    body: form,
    signal,
  })
  if (!response.ok) throw response
  return parseDocument(await response.json())
}

/** The files Cai can read in the live conversation, in the order they were shared. */
export async function getSharedDocuments(signal?: AbortSignal): Promise<SharedDocument[]> {
  const response = await fetch(DOCUMENTS_ENDPOINT, { credentials: 'same-origin', signal })
  if (!response.ok) throw response
  const body: unknown = await response.json()
  if (typeof body !== 'object' || body === null || !('documents' in body) || !Array.isArray(body.documents)) {
    throw new Error('Unexpected Cai documents response')
  }
  return body.documents.map(parseDocument)
}

/** Stop including a shared file in future turns; the question it was sent with still shows it. */
export async function removeDocument(
  csrfToken: string,
  documentId: string,
  signal?: AbortSignal
): Promise<void> {
  const response = await fetch(`${DOCUMENTS_ENDPOINT}/${encodeURIComponent(documentId)}`, {
    method: 'DELETE',
    credentials: 'same-origin',
    headers: writeHeaders(csrfToken),
    signal,
  })
  if (!response.ok) throw response
}

/**
 * The mailboxes Cai can read. Each value is the provider's slug: its routes sit under it, and its connect
 * callback appends how the attempt ended under it as a query parameter.
 */
export enum MailProvider {
  Gmail = 'gmail',
  Outlook = 'outlook',
}

export const MAIL_PROVIDER_NAMES: Record<MailProvider, string> = {
  [MailProvider.Gmail]: 'Gmail',
  [MailProvider.Outlook]: 'Outlook',
}

function mailEndpoint(provider: MailProvider): string {
  return `/api/v1/integrations/${provider}`
}

/** The line Cai ends an answer with to offer connecting this mailbox; the page draws it as a button. */
export function mailOfferMarker(provider: MailProvider): string {
  return `[[connect-${provider}]]`
}

export type MailConnection = {
  /** This deployment holds the provider's OAuth credentials; without them there is nothing to connect. */
  configured: boolean
  connected: boolean
  /** The provider stopped honoring the grant, so the account has to connect again. */
  needsReconnect: boolean
  /** The connected mailbox address, or null when no mailbox has been connected. */
  emailAddress: string | null
  /** When the synced copy was last caught up; null until the first sync finishes. */
  syncedThrough: Date | null
}

export type MailStatus = { csrfToken: string; connection: MailConnection }

function parseMailConnection(provider: MailProvider, body: unknown): MailConnection {
  const fields = (typeof body === 'object' && body !== null ? body : {}) as Record<string, unknown>
  if (typeof fields.provider === 'string' && fields.provider !== provider) {
    return {
      configured: false,
      connected: false,
      needsReconnect: false,
      emailAddress: null,
      syncedThrough: null,
    }
  }
  const connection = (
    typeof fields.connection === 'object' && fields.connection !== null ? fields.connection : {}
  ) as Record<string, unknown>
  return {
    configured: connection.configured === true,
    connected: connection.state === 'ACTIVE',
    needsReconnect: connection.state === 'NEEDS_REAUTHORIZATION',
    emailAddress: typeof connection.company_id === 'string' ? connection.company_id : null,
    syncedThrough:
      typeof connection.books_synced_through === 'string' ? new Date(connection.books_synced_through) : null,
  }
}

/** Throws the raw Response on refusal: a 403 means the provider is switched off for this deployment. */
export async function getMailStatus(provider: MailProvider, signal?: AbortSignal): Promise<MailStatus> {
  const response = await fetch(`${mailEndpoint(provider)}/status`, { credentials: 'same-origin', signal })
  if (!response.ok) throw response
  const body: unknown = await response.json()
  const csrfToken = typeof body === 'object' && body !== null && 'csrf_token' in body ? body.csrf_token : null
  if (typeof csrfToken !== 'string')
    throw new Error(`Unexpected ${MAIL_PROVIDER_NAMES[provider]} status response`)
  return { csrfToken, connection: parseMailConnection(provider, body) }
}

/** Start connecting and return the provider's consent page, which sends the browser back to returnTo. */
export async function startMailConnect(
  provider: MailProvider,
  csrfToken: string,
  returnTo: string
): Promise<string> {
  const response = await fetch(`${mailEndpoint(provider)}/connect`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: writeHeaders(csrfToken),
    body: JSON.stringify({ return_to: returnTo }),
  })
  if (!response.ok) throw response
  const body: unknown = await response.json()
  const url =
    typeof body === 'object' && body !== null && 'authorization_url' in body ? body.authorization_url : null
  if (typeof url !== 'string') throw new Error(`Unexpected ${MAIL_PROVIDER_NAMES[provider]} connect response`)
  return url
}

/** Revoke the account's grant with the provider and return where the connection now stands. */
export async function disconnectMail(provider: MailProvider, csrfToken: string): Promise<MailConnection> {
  const response = await fetch(`${mailEndpoint(provider)}/disconnect`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: writeHeaders(csrfToken),
  })
  if (!response.ok) throw response
  return parseMailConnection(provider, await response.json())
}

function parseWebsiteLogin(value: unknown): WebsiteLogin {
  const login = (value ?? {}) as Record<string, unknown>
  if (typeof login.id !== 'string' || typeof login.origin !== 'string' || typeof login.url !== 'string') {
    throw new Error('Unexpected website login response')
  }
  const origin = new URL(login.origin)
  if (origin.protocol !== 'https:' || origin.origin !== login.origin) {
    throw new Error('Unexpected website login origin')
  }
  return {
    id: login.id,
    origin: login.origin,
    url: login.url,
    name: typeof login.name === 'string' ? login.name : '',
  }
}

export type SavedWebsites = {
  logins: WebsiteLogin[]
  /** Cai's own website, which Cai signs in to as the account without a saved login; null where it has none. */
  caiSite: string | null
}

export async function getWebsiteLogins(signal?: AbortSignal): Promise<SavedWebsites> {
  const response = await fetch(WEBSITE_LOGINS_ENDPOINT, { credentials: 'same-origin', signal })
  if (!response.ok) throw response
  const body = (await response.json()) as Record<string, unknown>
  if (!Array.isArray(body.logins)) throw new Error('Unexpected website logins response')
  return {
    logins: body.logins.map(parseWebsiteLogin),
    caiSite: typeof body.cai_site === 'string' ? body.cai_site : null,
  }
}

export async function saveWebsiteLogin(
  csrfToken: string,
  credentials: WebsiteCredentials,
  signal?: AbortSignal
): Promise<WebsiteLogin> {
  const response = await fetch(WEBSITE_LOGINS_ENDPOINT, {
    method: 'POST',
    credentials: 'same-origin',
    headers: writeHeaders(csrfToken),
    body: JSON.stringify(credentials),
    signal,
  })
  if (!response.ok) throw response
  const body = (await response.json()) as Record<string, unknown>
  return parseWebsiteLogin(body.login)
}

export async function removeWebsiteLogin(csrfToken: string, id: string, signal?: AbortSignal): Promise<void> {
  const response = await fetch(`${WEBSITE_LOGINS_ENDPOINT}/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    credentials: 'same-origin',
    headers: writeHeaders(csrfToken),
    signal,
  })
  if (!response.ok) throw response
}

/** Continues the paused question; no code means the person approved the sign-in from the website's email. */
export async function continueWebsiteSignIn(
  csrfToken: string,
  verificationId: string,
  code: string
): Promise<ActiveTurn> {
  const response = await fetch(`${WEBSITE_VERIFICATIONS_ENDPOINT}/${encodeURIComponent(verificationId)}`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: writeHeaders(csrfToken),
    body: JSON.stringify({ code }),
  })
  if (!response.ok) throw response
  const accepted = (await response.json()) as Record<string, unknown>
  if (typeof accepted.turn_id !== 'string' || typeof accepted.message !== 'string')
    throw new Error('Unexpected Cai turn response')
  return { turn_id: accepted.turn_id, message: accepted.message }
}

export async function cancelWebsiteSignIn(csrfToken: string, verificationId: string): Promise<void> {
  const response = await fetch(`${WEBSITE_VERIFICATIONS_ENDPOINT}/${encodeURIComponent(verificationId)}`, {
    method: 'DELETE',
    credentials: 'same-origin',
    headers: writeHeaders(csrfToken),
  })
  if (!response.ok) throw response
}

const TERMINAL_ENDPOINT = '/api/v1/integrations/terminal'
export const TERMINAL_OUTCOME_PARAM = 'terminal'
export const TERMINAL_OFFER_MARKER = '[[connect-terminal]]'
export const CAI_AGENT_IDEAS_PATH = `${CAI_AGENT_PATH}/ideas`
export const CAI_AGENT_CONNECT_PATH = `${CAI_AGENT_PATH}/connect`
export const CAI_AGENT_MEMORIES_PATH = `${CAI_AGENT_PATH}/memories`
export const CAI_AGENT_TASKS_PATH = `${CAI_AGENT_PATH}/tasks`

export enum TerminalSyncState {
  Ready = 'ready',
  NotReady = 'not_ready',
  Unavailable = 'unavailable',
  Disconnected = 'disconnected',
}

export type TerminalConnection = {
  configured: boolean
  connected: boolean
  needsReconnect: boolean
  providerName: string | null
  fleetSyncedThrough: Date | null
  syncState: TerminalSyncState
  upstreamSyncStatus: string | null
  /** Saved by the backend, unlike syncState: true until Cai records the first sync it announces in Chat. */
  firstSyncPending: boolean
}

export type TerminalStatus = { csrfToken: string; connection: TerminalConnection }

function parseTerminalConnection(body: unknown): TerminalConnection {
  const fields = (typeof body === 'object' && body !== null ? body : {}) as Record<string, unknown>
  if (fields.provider !== 'terminal') throw new Error('Unexpected Terminal provider')
  const connection = (
    typeof fields.connection === 'object' && fields.connection !== null ? fields.connection : {}
  ) as Record<string, unknown>
  const completedAt =
    typeof connection.fleet_synced_through === 'string' ? new Date(connection.fleet_synced_through) : null
  const syncState = Object.values(TerminalSyncState).find((state) => state === connection.sync_state)
  return {
    configured: connection.configured === true,
    connected: connection.state === 'ACTIVE',
    needsReconnect: connection.state === 'NEEDS_REAUTHORIZATION',
    providerName: typeof connection.provider_name === 'string' ? connection.provider_name : null,
    fleetSyncedThrough: completedAt && Number.isFinite(completedAt.getTime()) ? completedAt : null,
    syncState: syncState ?? TerminalSyncState.Unavailable,
    upstreamSyncStatus:
      typeof connection.upstream_sync_status === 'string' ? connection.upstream_sync_status : null,
    firstSyncPending: connection.first_sync_pending === true,
  }
}

/** Read the account's Terminal connection and last confirmed fleet sync. */
export async function getTerminalStatus(signal?: AbortSignal): Promise<TerminalStatus> {
  const response = await fetch(`${TERMINAL_ENDPOINT}/status`, { credentials: 'same-origin', signal })
  if (!response.ok) throw response
  const body: unknown = await response.json()
  const csrfToken = typeof body === 'object' && body !== null && 'csrf_token' in body ? body.csrf_token : null
  if (typeof csrfToken !== 'string') throw new Error('Unexpected Terminal status response')
  return { csrfToken, connection: parseTerminalConnection(body) }
}

/** Open hosted Terminal Link, which returns to the given Cai path. */
export async function startTerminalConnect(csrfToken: string, returnTo: string): Promise<string> {
  const response = await fetch(`${TERMINAL_ENDPOINT}/connect`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: writeHeaders(csrfToken),
    body: JSON.stringify({ return_to: returnTo }),
  })
  if (!response.ok) throw response
  const body: unknown = await response.json()
  const url =
    typeof body === 'object' && body !== null && 'authorization_url' in body ? body.authorization_url : null
  if (typeof url !== 'string') throw new Error('Unexpected Terminal connect response')
  return url
}

/** Archive the account's Terminal connection before retiring local access. */
export async function disconnectTerminal(csrfToken: string): Promise<TerminalConnection> {
  const response = await fetch(`${TERMINAL_ENDPOINT}/disconnect`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: writeHeaders(csrfToken),
  })
  if (!response.ok) throw response
  return parseTerminalConnection(await response.json())
}
